'use client';

// Imperative market-lifecycle-modal store (spec 112-market-lifecycle-modals).
// Mounts a single `<MarketLifecycleModal />` at the AppShell root; the
// page-scoped trigger hook (`useMarketLifecycleModalTrigger`) calls
// `openModal(payload)` when a graduation / resolution lifecycle event is
// eligible. Mirrors the `share-modal` → `GlobalShareModal` pattern so the
// modal survives navigation and renders above app chrome.

import type { StoreApi, UseBoundStore } from 'zustand';

import { createTrackedStore } from '@/lib/dev/zustand-registry';

/** Keep a live transition claimed across wallet hydration and route remounts. */
export function liveGraduationKey(
  viewerId: string,
  marketPubkey: string,
  isDemoMode: boolean,
): string {
  return `${isDemoMode ? 'demo' : 'main'}:${viewerId}:${marketPubkey}`;
}

/** Lifecycle boundary that triggered the modal. */
export type LifecycleStage = 'graduation' | 'resolution';

/** The concrete modal variants (spec FR-009/FR-010). */
export type LifecycleVariant =
  | 'graduation-generic'
  | 'graduation-backer'
  | 'graduation-creator'
  | 'resolution-won-backer'
  | 'resolution-won-nonbacker'
  | 'resolution-lost-backer'
  | 'resolution-lost-nonbacker'
  | 'resolution-not-holder';

/** Claim affordance state for the won variants (derived from `usePrizePool`). */
export interface LifecycleClaimState {
  /** User holds the winning outcome and a claim exists. */
  claimable: boolean;
  /** Claim root is finalized — the claim CTA can be actioned. */
  finalized: boolean;
  /** User has already claimed. */
  claimed: boolean;
}

/**
 * Everything the global modal needs to render a variant. Passed in by the
 * page trigger so the global modal needs no page/market context of its own.
 */
export interface LifecycleModalPayload {
  marketPubkey: string;
  stage: LifecycleStage;
  variant: LifecycleVariant;
  /** Whether the connected wallet backed this market (toggles backer CTA). */
  isBacker: boolean;
  /** Winning outcome index — null for the graduation stage. */
  winnerOutcomeIndex: number | null;
  winningOutcomeLabel: string | null;
  winningOutcomeImageUrl: string | null;
  winningOutcomeImageNsfw: boolean;
  /** Won-variant claim affordance; null when not applicable. */
  claim: LifecycleClaimState | null;
  /** Timed event start for truthful graduation copy before trading opens. */
  tradingStartAt?: string;
  tradingObservedAtMs?: number;
}

interface MarketLifecycleModalState {
  open: boolean;
  liveGraduations: Record<string, true>;
  openLiveGraduation: (key: string, payload: LifecycleModalPayload) => boolean;
  payload: LifecycleModalPayload | null;
  ownerKey: string | null;
  openModal: (payload: LifecycleModalPayload, ownerKey?: string) => void;
  refreshOpenModal: (payload: LifecycleModalPayload) => void;
  closeModal: () => void;
}

export const useMarketLifecycleModalStore: UseBoundStore<StoreApi<MarketLifecycleModalState>> =
  createTrackedStore<MarketLifecycleModalState>('useMarketLifecycleModalStore')((set, get) => ({
    open: false,
    liveGraduations: {},
    payload: null,
    ownerKey: null,
    openLiveGraduation(key: string, payload: LifecycleModalPayload): boolean {
      if (get().liveGraduations[key] === true) return false;
      // Claim and open together so duplicate observers and route remounts cannot replay.
      // Session-only: persistent cold-visit markers must not suppress a live graduation.
      set((state) => ({
        liveGraduations: { ...state.liveGraduations, [key]: true },
        open: true,
        ownerKey: key,
        payload,
      }));
      return true;
    },
    openModal(payload: LifecycleModalPayload, ownerKey?: string): void {
      set({ open: true, payload, ownerKey: ownerKey ?? null });
    },
    refreshOpenModal(payload: LifecycleModalPayload): void {
      set((state) => {
        if (
          !state.open ||
          state.payload === null ||
          state.payload.marketPubkey !== payload.marketPubkey ||
          state.payload.stage !== payload.stage
        ) {
          return state;
        }
        return { payload };
      });
    },
    closeModal(): void {
      // Keep `payload` populated through the close animation so the dialog
      // body doesn't blank during exit; the next open replaces it.
      set({ open: false });
    },
  }));
